import { TABLE_TYPE } from 'components/PayrollTable/constants';
import intl from 'react-intl-universal';
import Button from '@mui/material/Button';
import { getIssuePriority } from 'shared/constants/issues';
import PropTypes from 'prop-types';
import {
  createWagesCSVContent,
  createDownloadLink,
  createCSVKenshinCSVContent,
} from '../shared/csv';

export const getSummaryTableData = (myWageData, myWageDataKenshin) => {
  const totalWageMap = new Map();
  myWageData.forEach((wage, _) => {
    const organizationId = wage.organization.id;
    const totalWage = totalWageMap.get(organizationId);
    if (!totalWage) {
      const newTotalWage = {
        organizationName: wage.organization.name,
        organizationId: organizationId,
        wageAmount: wage.currentWage ? wage.currentWage : wage.originalWage,
      };
      totalWageMap.set(organizationId, newTotalWage);
    } else {
      const newTotalWage = {
        organizationName: totalWage.organizationName,
        organizationId: organizationId,
        wageAmount: wage.currentWage
          ? totalWage.wageAmount + wage.currentWage
          : totalWage.wageAmount + wage.originalWage,
      };
      totalWageMap.set(organizationId, newTotalWage);
    }
  });

  myWageDataKenshin.forEach((wage, _) => {
    const organizationId = wage.orgId;
    const totalWage = totalWageMap.get(organizationId);
    if (!totalWage) {
      const newTotalWage = {
        organizationName: wage.orgName,
        organizationId: organizationId,
        wageAmount: wage.currentWage ? wage.currentWage : wage.originalWage,
      };
      totalWageMap.set(organizationId, newTotalWage);
    } else {
      const newTotalWage = {
        organizationName: totalWage.organizationName,
        organizationId: totalWage.organizationId,
        wageAmount: wage.currentWage
          ? totalWage.wageAmount + wage.currentWage
          : totalWage.wageAmount + wage.originalWage,
      };
      totalWageMap.set(organizationId, newTotalWage);
    }
  });

  const data = [];
  totalWageMap.forEach((value) => {
    const { organizationName, organizationId, wageAmount } = value;
    data.push({
      organizationName: organizationName,
      totalWage: wageAmount,
      details: {
        organizationName: organizationName,
        organizationId: organizationId,
      },
      download: {
        organizationName: organizationName,
        organizationId: organizationId,
      },
    });
  });
  return data;
};

export const getSummaryTableColumns = (
  setSelectedOrganizationName,
  setSelectedOrganizationId,
  setTableType,
  myWageData,
  myWageDataKenshin,
  doesHaveKenshinPermission
) => [
  OrganizationNameColumn(),
  TotalWageColumn(),
  DetailsColumn({
    setSelectedOrganizationName,
    setSelectedOrganizationId,
    setTableType,
    doesHaveKenshinPermission,
  }),
  DownloadColumn({ myWageData, myWageDataKenshin, doesHaveKenshinPermission }),
];

export const OrganizationNameColumn = () => {
  return {
    accessorKey: 'organizationName',
    header: intl.get('my_wage_label_column_organization_name'),
    enableColumnFilter: true,
  };
};

export const TotalWageColumn = () => {
  return {
    accessorKey: 'totalWage',
    header: intl.get('my_wage_label_column_total_wage'),
    enableColumnFilter: false,
    enableSorting: true,
    Cell: totalWageCell,
  };
};

const totalWageCell = ({ renderedCellValue }) => {
  const totalWage = renderedCellValue;
  return (
    <div>
      <p>￥{totalWage}</p>
    </div>
  );
};

totalWageCell.propTypes = {
  renderedCellValue: PropTypes.number.isRequired,
};

export const DetailsColumn = ({
  setSelectedOrganizationName,
  setSelectedOrganizationId,
  setTableType,
  doesHaveKenshinPermission,
}) => {
  return {
    accessorKey: 'details',
    header: intl.get('my_wage_label_column_details'),
    enableColumnFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTableBodyCellProps: {
      onClick: (e) => e.stopPropagation(),
    },
    Cell: ({ renderedCellValue }) => {
      return detailsCell({
        renderedCellValue,
        setSelectedOrganizationName,
        setSelectedOrganizationId,
        setTableType,
        doesHaveKenshinPermission,
      });
    },
  };
};

const detailsCell = ({
  renderedCellValue,
  setSelectedOrganizationName,
  setSelectedOrganizationId,
  setTableType,
  doesHaveKenshinPermission,
}) => {
  const { organizationName, organizationId } = renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setSelectedOrganizationName(organizationName);
          setSelectedOrganizationId(organizationId);
          setTableType(TABLE_TYPE.DETAILED);
        }}>
        {intl.get('my_wage_button_go_to_details_seimitsu')}
      </Button>
      {doesHaveKenshinPermission && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedOrganizationName(organizationName);
            setSelectedOrganizationId(organizationId);
            setTableType(TABLE_TYPE.DETAILED_KENSHIN);
          }}>
          {intl.get('my_wage_button_go_to_details_kenshin')}
        </Button>
      )}
    </div>
  );
};

detailsCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  setSelectedOrganizationName: PropTypes.func.isRequired,
  setSelectedOrganizationId: PropTypes.func.isRequired,
  setTableType: PropTypes.func.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};

export const DownloadColumn = ({
  myWageData,
  myWageDataKenshin,
  doesHaveKenshinPermission,
}) => {
  const exportToCSV = (organizationId, organizationName) => {
    const selectedWageData = myWageData.filter(
      (wage) => wage.organization.id === organizationId
    );
    const csvFilename = `my_wage-from-${organizationName}.csv`;
    const csvContent = createWagesCSVContent(selectedWageData, false);
    createDownloadLink(csvContent, csvFilename);
  };

  const exportToCSVKenshin = (organizationId, organizationName) => {
    const selectedWageData = myWageDataKenshin.filter(
      (wage) => wage.orgId === organizationId
    );
    const csvFilename = `my_wage-from-${organizationName}.csv`;
    const csvContent = createCSVKenshinCSVContent(selectedWageData, false);
    createDownloadLink(csvContent, csvFilename);
  };

  return {
    accessorKey: 'download',
    header: intl.get('my_wage_label_column_download'),
    enableColumnFilter: false,
    enableSorting: false,
    enableColumnActions: false,
    muiTableBodyCellProps: {
      onClick: (e) => e.stopPropagation(),
    },
    Cell: ({ renderedCellValue }) => {
      return downloadCell({
        renderedCellValue,
        exportToCSV,
        exportToCSVKenshin,
        doesHaveKenshinPermission,
      });
    },
  };
};

const downloadCell = ({
  renderedCellValue,
  exportToCSV,
  exportToCSVKenshin,
  doesHaveKenshinPermission,
}) => {
  const { organizationName, organizationId } = renderedCellValue;
  return (
    <div>
      <div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            exportToCSV(organizationId, organizationName);
          }}>
          {intl.get('my_wage_button_download_csv_seimitsu')}
        </Button>
        {doesHaveKenshinPermission && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              exportToCSVKenshin(organizationId, organizationName);
            }}>
            {intl.get('my_wage_button_download_csv_kenshin')}
          </Button>
        )}
      </div>
    </div>
  );
};

downloadCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  exportToCSV: PropTypes.func.isRequired,
  exportToCSVKenshin: PropTypes.func.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};
